import { TAG_TYPES, FormatResponse } from "types";
import { baseApi } from "./baseApi";
import { CreateStaffPayload, StaffRecord } from "types/staff.types";
import { getQueryParams, QueryParams } from "types/queryParams.types";
import { UserModel } from "models/UserModel";

const URL_STAFFS = "staffs";

const staffApiService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // Get all staff
    getAllStaff: builder.query<
      FormatResponse<{ count: number; rows: StaffRecord[] }>,
      { query: QueryParams }
    >({
      query: (params) => {
        const queryParams = params?.query || params;
        const queryString = getQueryParams(queryParams);
        return {
          url: queryString ? `${URL_STAFFS}?${queryString}` : URL_STAFFS,
          method: "GET",
        };
      },
      providesTags: [{ type: TAG_TYPES.STAFF, id: "LIST" }],
    }),

    // Get staff by ID
    getStaffById: builder.query<FormatResponse<StaffRecord>, { id: string }>({
      query: ({ id }) => ({
        url: `${URL_STAFFS}/${id}`,
        method: "GET",
      }),
      providesTags: (_result, _error, { id }) => [
        { type: TAG_TYPES.STAFF, id },
      ],
    }),

    // Create staff
    createStaff: builder.mutation<
      FormatResponse<StaffRecord>,
      CreateStaffPayload
    >({
      query: (data) => ({
        url: URL_STAFFS,
        method: "POST",
        data,
      }),
      invalidatesTags: [{ type: TAG_TYPES.STAFF, id: "LIST" }],
    }),

    // Update staff
    updateStaff: builder.mutation<
      { statusCode: number; success: boolean; data: StaffRecord },
      { id: string } & Omit<Partial<StaffRecord>, "user"> & {
          user?: Partial<UserModel>;
        }
    >({
      query: ({ id, ...data }) => ({
        url: `${URL_STAFFS}/${id}`,
        method: "PATCH",
        data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: TAG_TYPES.STAFF, id },
        { type: TAG_TYPES.STAFF, id: "LIST" },
      ],
    }),

    // Delete staff
    deleteStaff: builder.mutation<
      FormatResponse<{ success: boolean }>,
      { id: string }
    >({
      query: ({ id }) => ({
        url: `${URL_STAFFS}/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TAG_TYPES.STAFF, id },
        { type: TAG_TYPES.STAFF, id: "LIST" },
      ],
    }),
  }),
});

export const {
  useGetStaffByIdQuery,
  useGetAllStaffQuery,
  useCreateStaffMutation,
  useUpdateStaffMutation,
  useDeleteStaffMutation,
} = staffApiService;
